import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

import { getAllDNROrders } from "../services/api";
import LsService, { storageKey } from "../services/localstorage";
import {
  headerBoxStyle,
  lrgScreenStyle,
  rightInnerBoxStyle,
} from "../components/panelComponents/panelStyles";
import DNRMainNavHeader from "../components/panelComponents/DNRMainNavHeader";

const MIN_DATE = "2025-01-01";
const TODAY = new Date().toISOString().split("T")[0];

/* Convert YYYY-MM-DD → YYYY-MM-DD HH:mm:ss */
const toApiDateTime = (dateStr, endOfDay = false) => {
  if (!dateStr) return "";
  return endOfDay ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
};

function DNRBillingExportPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingLastMonth, setLoadingLastMonth] = useState(false);
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [countMonthly, setCountMonthly] = useState(0);
  const [countLastMonth, setCountLastMonth] = useState(0);

  const [yearSelected, setYearSelected] = useState("");
  const [monthSelected, setMonthSelected] = useState("");

  const [totals, setTotals] = useState({
    gst: 0,
    cgst: 0,
    sgst: 0,
    amount: 0,
  });

  const currentYear = new Date().getFullYear();

  const yearsList = Array.from(
    { length: currentYear - 2025 + 1 },
    (_, i) => 2025 + i,
  );

  const handleChangeYear = (e) => {
    setYearSelected(e.target.value);
  };

  const handleChangeMonth = (e) => {
    setMonthSelected(e.target.value);
  };

  const calculateTotals = (orders) => {
    let gst = 0;
    let cgst = 0;
    let sgst = 0;
    let amount = 0;

    orders.forEach((order) => {
      const g = Number(order.gst || 0);
      const t = Number(order.total_amount || 0);

      gst += g;
      cgst += g / 2;
      sgst += g / 2;
      amount += t;
    });

    setTotals({
      gst: gst.toFixed(2),
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      amount: amount.toFixed(2),
    });
  };

  const formatDateForSheet = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-CA"); // YYYY-MM-DD
  };

  const getMonthLastDate = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const generateExcel = (orders, start, end) => {
    if (!orders.length) return;

    const exportData = [];

    orders.forEach((order, i) => {
      const cart = order.BillAppOrderProducts || [];

      const gst = Number(order.gst || 0);
      const cgst = gst / 2;
      const sgst = gst / 2;
      const total = Number(order.total_amount || 0);

      cart.forEach((item, idx) => {
        const product = item.BillAppProduct || {};

        exportData.push({
          S_No: idx === 0 ? i + 1 : "",
          Date_of_Inv:
            idx === 0 ? new Date(order.order_date).toLocaleString("en-IN") : "",

          Inv_No: idx === 0 ? `INV${order.order_id}` : "",
          Payment_Type: idx === 0 ? order.payment_method : "",

          CGST: idx === 0 ? `${cgst.toFixed(2)}` : "",
          SGST: idx === 0 ? `${sgst.toFixed(2)}` : "",
          GST: idx === 0 ? `${gst.toFixed(2)}` : "",

          Inv_Value: idx === 0 ? `${total.toFixed(2)}` : "",

          Material_Name: product.product_name || "",
          Quantity: item.quantity || 1,
          Items_Total_Amt: `${item.price || 0}`,
        });
      });
    });

    /* -------- Sheet 1 ---------- */

    const ws1 = XLSX.utils.json_to_sheet(exportData);

    ws1["!cols"] = [
      { wch: 8 },
      { wch: 20 },
      { wch: 10 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 10 },
      { wch: 15 },
    ];

    /* -------- Sheet 2 (Totals) ---------- */

    const totalSheet = [
      {
        Total_CGST: totals.cgst,
        Total_SGST: totals.sgst,
        Total_GST: totals.gst,
        Total_Inv_Value: totals.amount,
      },
    ];

    const ws2 = XLSX.utils.json_to_sheet(totalSheet);

    /* -------- Workbook ---------- */

    const wb = XLSX.utils.book_new();

    const sheetName = `${start} to ${end}`.replace(/[\\\/\?\*\[\]\:]/g, "-");

    XLSX.utils.book_append_sheet(wb, ws1, sheetName);
    XLSX.utils.book_append_sheet(wb, ws2, "DNR_Totals");

    XLSX.writeFile(wb, `DNR_Billing_${Date.now()}.xlsx`);
  };

  const resetTotals = () => {
    setTotals({
      gst: 0,
      cgst: 0,
      sgst: 0,
      amount: 0,
    });
    setOrders([]);
    setCount(0);
    setCountMonthly(0);
    setCountLastMonth(0);
  };

  const fetchDataWithDatesLastMonth = async (start, end) => {
    try {
      setLoadingLastMonth(true);
      resetTotals();
      setMonthSelected("");
      setYearSelected("");

      const userData = LsService.getItem(storageKey);

      if (!userData?.user_id) return;

      const reqParams = {
        page: 1,
        limit: 10000,
      };

      const reqBody = {
        user_id: userData.user_id,
        startDate: toApiDateTime(start),
        endDate: toApiDateTime(end, true),
      };

      const response = await getAllDNROrders(reqParams, reqBody);

      const apiOrders = response?.data?.data || [];

      setOrders(apiOrders);
      setCountLastMonth(apiOrders.length);

      if (apiOrders.length) {
        calculateTotals(apiOrders);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLastMonth(false);
    }
  };

  const fetchDataWithDatesMonthly = async (start, end) => {
    try {
      setLoadingMonthly(true);
      resetTotals();

      const userData = LsService.getItem(storageKey);

      if (!userData?.user_id) return;

      const reqParams = {
        page: 1,
        limit: 10000,
      };

      const reqBody = {
        user_id: userData.user_id,
        startDate: toApiDateTime(start),
        endDate: toApiDateTime(end, true),
      };

      const response = await getAllDNROrders(reqParams, reqBody);

      const apiOrders = response?.data?.data || [];

      setOrders(apiOrders);
      setCountMonthly(apiOrders.length);

      if (apiOrders.length) {
        calculateTotals(apiOrders);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchDataWithDates = async (start, end) => {
    try {
      setLoading(true);
      resetTotals();
      setMonthSelected("");
      setYearSelected("");

      const userData = LsService.getItem(storageKey);

      if (!userData?.user_id) return;

      const reqParams = {
        page: 1,
        limit: 10000,
      };

      const reqBody = {
        user_id: userData.user_id,
        startDate: toApiDateTime(start),
        endDate: toApiDateTime(end, true),
      };

      const response = await getAllDNROrders(reqParams, reqBody);

      const apiOrders = response?.data?.data || [];

      setOrders(apiOrders);
      setCount(apiOrders.length);

      if (apiOrders.length) {
        calculateTotals(apiOrders);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onGetLastMonth = async () => {
    const today = new Date();

    const last31Days = new Date(today.getTime() - 31 * 24 * 60 * 60 * 1000);

    const start = last31Days.toISOString().split("T")[0];
    const end = today.toISOString().split("T")[0];

    // Update UI
    setStartDate(start);
    setEndDate(end);

    // Fetch using DIRECT values (not state)
    await fetchDataWithDatesLastMonth(start, end);
  };

  const onLastMonthDownload = () => {
    const start = formatDateForSheet(startDate);
    const end = formatDateForSheet(endDate);

    generateExcel(orders, start, end);
  };

  const onGetMonthlyData = async () => {
    const lastDate = getMonthLastDate(yearSelected, monthSelected);

    const start = `${yearSelected}-${String(monthSelected).padStart(2, "0")}-01`;
    const end = `${yearSelected}-${String(monthSelected).padStart(2, "0")}-${lastDate}`;

    setStartDate(start);
    setEndDate(end);

    await fetchDataWithDatesMonthly(start, end);
  };

  const onMonthlyDownload = () => {
    const start = formatDateForSheet(startDate);
    const end = formatDateForSheet(endDate);

    generateExcel(orders, start, end);
  };

  /* Download Excel */
  const onDownload = () => {
    const start = formatDateForSheet(startDate);
    const end = formatDateForSheet(endDate);

    generateExcel(orders, start, end);
  };

  return (
    <Box>
      {/* Large screen view */}
      <Box sx={lrgScreenStyle}>
        <Box sx={headerBoxStyle}>
          <DNRMainNavHeader
            headerTitle="CA Reports Management"
            headerNavStartTitle="Home / Admin Dashboard"
            homeNavigate="/dnr_super_admin"
            headerNavEndTitle="Reports Management"
          />
        </Box>

        <Box sx={rightInnerBoxStyle}>
          <Box
            sx={{
              width: "96%",
              p: 2,
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {(count > 0 || countMonthly > 0 || countLastMonth > 0) && (
                <Box sx={{ p: 2 }}>
                  <Paper sx={{ p: 2, borderRadius: "10px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h6">
                        Total Amount: <strong>₹{totals.amount}</strong>
                      </Typography>

                      <Typography variant="h6">
                        Total GST: <strong>₹{totals.gst}</strong>
                      </Typography>

                      <Typography variant="h6">
                        Total CGST: <strong>₹{totals.cgst}</strong>
                      </Typography>

                      <Typography variant="h6">
                        Total SGST: <strong>₹{totals.sgst}</strong>
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
              <Box sx={{ p: 2 }}>
                {/* <Box p={4}> */}
                <Paper elevation={3} sx={{ p: 4, borderRadius: "10px" }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    mb={2}
                    textAlign="center"
                  >
                    Export Date to Date Report 
                  </Typography>

                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Start Date */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        inputProps={{
                          min: MIN_DATE,
                          max: TODAY,
                        }}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={loading}
                      />
                    </Grid>

                    {/* End Date */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        inputProps={{
                          min: startDate || MIN_DATE,
                          max: TODAY,
                        }}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!startDate || loading}
                      />
                    </Grid>

                    {/* Get Data */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={() => fetchDataWithDates(startDate, endDate)}
                        disabled={!startDate || !endDate || loading}
                      >
                        {loading ? <CircularProgress size={24} /> : "Get Data"}
                      </Button>
                    </Grid>

                    {/* Download */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={onDownload}
                        disabled={count === 0 || loading}
                      >
                        Download excel <DownloadIcon sx={{ ml: 1 }} />
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Count Display */}
                  <Box mt={2} textAlign="center">
                    <Typography variant="h6">
                      Records Found: <strong>{count}</strong>
                    </Typography>
                  </Box>
                </Paper>
                {/* </Box> */}
              </Box>

              <Box sx={{ p: 2 }}>
                {/* <Box p={4}> */}
                <Paper elevation={3} sx={{ p: 4, borderRadius: "10px" }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    mb={2}
                    textAlign="center"
                  >
                    Export Monthly Report
                  </Typography>

                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Year */}
                    <Grid item sx={{ minWidth: "150px" }}>
                      <FormControl fullWidth>
                        <InputLabel>Select Year</InputLabel>
                        <Select
                          value={yearSelected}
                          label="Select Year"
                          onChange={handleChangeYear}
                        >
                          {[...yearsList].reverse().map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Monthly */}
                    <Grid item sx={{ minWidth: "200px" }}>
                      <FormControl fullWidth>
                        <InputLabel>Select Month</InputLabel>
                        <Select
                          value={monthSelected}
                          label="Select Month"
                          onChange={handleChangeMonth}
                          disabled={!yearSelected}
                        >
                          <MenuItem value={1}>January</MenuItem>
                          <MenuItem value={2}>February</MenuItem>
                          <MenuItem value={3}>March</MenuItem>
                          <MenuItem value={4}>April</MenuItem>
                          <MenuItem value={5}>May</MenuItem>
                          <MenuItem value={6}>June</MenuItem>
                          <MenuItem value={7}>July</MenuItem>
                          <MenuItem value={8}>August</MenuItem>
                          <MenuItem value={9}>September</MenuItem>
                          <MenuItem value={10}>October</MenuItem>
                          <MenuItem value={11}>November</MenuItem>
                          <MenuItem value={12}>December</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Get Data */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={() => onGetMonthlyData()}
                        disabled={
                          !yearSelected || !monthSelected || loadingMonthly
                        }
                      >
                        {loadingMonthly ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Get Monthly Data"
                        )}
                      </Button>
                    </Grid>

                    {/* Download */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => onMonthlyDownload()}
                        disabled={countMonthly === 0 || loadingMonthly}
                      >
                        Download Monthly excel <DownloadIcon sx={{ ml: 1 }} />
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Count Display */}
                  <Box mt={2} textAlign="center">
                    <Typography variant="h6">
                      Records Found: <strong>{countMonthly}</strong>
                    </Typography>
                  </Box>
                </Paper>
                {/* </Box> */}
              </Box>

              <Box sx={{ p: 2 }}>
                {/* <Box p={4}> */}

                <Paper elevation={3} sx={{ p: 4, borderRadius: "10px" }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    mb={2}
                    textAlign="center"
                  >
                    Export Last Month Report
                  </Typography>

                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Last 31 days */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={() => onGetLastMonth()}
                        disabled={loadingLastMonth}
                      >
                        {loadingLastMonth ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Get Last Month Data"
                        )}
                      </Button>
                    </Grid>

                    {/* Download */}
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => onLastMonthDownload()}
                        disabled={countLastMonth === 0 || loadingLastMonth}
                      >
                        Download Last Month Excel{" "}
                        <DownloadIcon sx={{ ml: 1 }} />
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Count Display */}
                  <Box mt={2} textAlign="center">
                    <Typography variant="h6">
                      Records Found: <strong>{countLastMonth}</strong>
                    </Typography>
                  </Box>
                </Paper>
                {/* </Box> */}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default DNRBillingExportPage;
